"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { caseStudies } from "@/lib/data/site";
import { SectionHeader } from "@/components/ui/section-header";
import { useCursor } from "@/providers/cursor-provider";
import { CaseStudyVisual, caseStudyVisualKey } from "@/components/home/case-study-visual";

export function CaseStudies() {
  const { setLabel } = useCursor();

  return (
    <section className="relative py-16 sm:py-24 md:py-32">
      <div className="container-wide">
        <SectionHeader
          eyebrow="Case Studies"
          title={"ENGAGEMENT PATTERNS\nIN PRODUCTION"}
          description="Selected ways we help enterprises move from AI ambition to governed, durable systems — with client details kept confidential where required."
        />

        <div className="mt-14 space-y-6">
          {caseStudies.map((study, index) => (
            <motion.article
              key={study.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: index * 0.08 }}
              className="group grid overflow-hidden rounded-[2rem] border border-border bg-card lg:grid-cols-[0.9fr_1.1fr]"
              onMouseEnter={() => setLabel("VIEW")}
              onMouseLeave={() => setLabel(null)}
            >
              <CaseStudyVisual
                variant={caseStudyVisualKey(study.industry)}
                industry={study.industry}
                client={study.client}
                className="transition duration-700 group-hover:brightness-110"
              />
              <div className="p-7 md:p-10">
                <h3 className="font-display text-3xl tracking-tight text-foreground md:text-4xl">{study.title}</h3>
                <div className="mt-6 grid gap-4 text-sm md:grid-cols-2">
                  <div>
                    <p className="mb-1 text-xs uppercase tracking-[0.16em] text-muted">Challenge</p>
                    <p className="leading-relaxed text-foreground/75">{study.challenge}</p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs uppercase tracking-[0.16em] text-muted">Solution</p>
                    <p className="leading-relaxed text-foreground/75">{study.solution}</p>
                  </div>
                </div>
                <div className="mt-6 flex flex-wrap gap-2">
                  {study.technology.map((tech) => (
                    <span key={tech} className="rounded-full border border-border px-3 py-1 text-xs">
                      {tech}
                    </span>
                  ))}
                </div>
                <p className="mt-6 text-sm text-foreground/80">{study.impact}</p>
                <Link href={study.href} className="mt-6 inline-flex items-center gap-2 text-sm font-medium hover:text-accent">
                  View Case Study <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
